// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { TextFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreAnchorMenu from "@store/Library/Components/Foundation/StoreAnchorMenu";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useLayoutEffect, useState } from "react";
import { css } from "styled-components";

interface RowModel {
    fields: {
        linkTitle: string;
        isOpen: boolean;
    };
}

const enum fields {
    rows = "rows",
    isOpenByDefault = "isOpenByDefault",
    hideAnchorMenu = "hideAnchorMenu",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.rows]: RowModel[];
        [fields.isOpenByDefault]: boolean;
        [fields.hideAnchorMenu]: boolean;
    };
}

type Props = OwnProps;

export interface CommonAccordionStyles {
    anchorMenuWrapper?: InjectableCss;
    accordionWrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
}

export const commonAccordionStyles: CommonAccordionStyles = {
    anchorMenuWrapper: {
        css: css`
            margin-bottom: ${pxToRem(30)};

            ${breakpoint("md")} {
                margin-bottom: ${pxToRem(40)};
            }
        `,
    },
    accordionWrapper: {
        css: css`
            ${siteWidthStyles()}
        `,
    },
    accordionSection: {
        titleTypographyProps: {
            forwardAs: "h2",
        },
    },
};

const styles = commonAccordionStyles;

export const commonAccordionAnchorId = "commonAccordionAnchorId";
export const commonAccordionWrapperId = "commonAccordionWrapperId";

const CommonAccordion: FC<Props> = ({ id, fields: { rows, isOpenByDefault, hideAnchorMenu } }) => {
    const [accordionRows, setAccordionRows] = useState<RowModel[]>([]);

    useLayoutEffect(() => {
        const newRows: RowModel[] = rows.map(row => ({
            fields: {
                ...row.fields,
                isOpen: isOpenByDefault,
            },
        }));

        setAccordionRows(newRows);
    }, [rows]);

    const onToggleHandler = (expanded: boolean, linkTitle: string) => {
        const newRows: RowModel[] = accordionRows.map(row => {
            if (row.fields.linkTitle === linkTitle) {
                return {
                    fields: {
                        ...row.fields,
                        isOpen: !expanded,
                    },
                };
            }

            return row;
        });

        setAccordionRows(newRows);
    };

    if (!accordionRows) {
        return null;
    }

    return (
        <>
            {!hideAnchorMenu && (
                <StyledWrapper {...styles.anchorMenuWrapper} id={commonAccordionAnchorId}>
                    <StoreAnchorMenu links={accordionRows} />
                </StyledWrapper>
            )}
            <StyledWrapper id={commonAccordionWrapperId} {...styles.accordionWrapper}>
                <Accordion headingLevel={2}>
                    {accordionRows?.map((accordionRow: RowModel, index) => {
                        const rowId = `${accordionRow.fields.linkTitle}-${index}`;
                        return (
                            <StyledWrapper key={accordionRow.fields.linkTitle}>
                                <AccordionSection
                                    title={accordionRow.fields.linkTitle}
                                    headerProps={{ id: accordionRow.fields.linkTitle, headingLevel: 2 }}
                                    uid={accordionRow.fields.linkTitle}
                                    onTogglePanel={expanded => onToggleHandler(expanded, accordionRow.fields.linkTitle)}
                                    expanded={accordionRow.fields.isOpen}
                                    {...styles.accordionSection}
                                >
                                    <Zone contentId={id} zoneName={`Row-${rowId}`} />
                                </AccordionSection>
                            </StyledWrapper>
                        );
                    })}
                </Accordion>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CommonAccordion,
    definition: {
        group: "Common",
        displayName: "Accordion",
        fieldDefinitions: [
            {
                name: fields.rows,
                editorTemplate: "ListField",
                getDisplay: ({ fields: { linkTitle } }) => linkTitle,
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "linkTitle",
                        displayName: "Row Title",
                        editorTemplate: "TextField",
                        fieldType: "Translatable",
                        defaultValue: "",
                        isRequired: true,
                    } as TextFieldDefinition,
                ],
            },
            {
                name: fields.isOpenByDefault,
                displayName: "Open all accordions sections by default",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.hideAnchorMenu,
                displayName: "Hide Anchor Menu",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
